/* eslint-disable @typescript-eslint/no-empty-interface */
import * as React from 'react';

import { IPhotoSchema, IVideoSchema, TOfferType } from '@cian/api-models/offer-card/v1/get-offer-data';
import { trackFullScreenOpen } from '../tracking';
import { FullScreenControlsContainer } from './full_screen_controls/container';

import * as styles from './fotorama.css';
import {
  calculateParametersCombination,
  getHtmlFotoramaComponent,
} from '../../../../browser/services/finance-widgets/offer-in-fotorama';

export interface IFotoramaStoreProps {
  priceTotalRur: number | null;
  isNewBuilding: boolean;
  offerType: TOfferType | null;
  isApartments: boolean | null;
  photos: IPhotoSchema[];
  videos: IVideoSchema[];
  showElementId: number | undefined;
  isMicrodataExperimentEnable: boolean;
  floorPlanImage?: string;
  isFullScreen: boolean;
}

export interface IFotoramaDispatchProps {
  onInitialized(): void;
  onPlayVideo(): void;
  onStopVideo(): void;
  onElementShowed(): void;
  onShowFullScreen(i: number): void;
  onHideFullScreen(): void;
}

export interface IFotoramaState {}

export interface IFotoramaAPI {
  activeIndex: number;

  show(index: number): void;
  cancelFullScreen(): void;
  playVideo(): void;
  splice(index: number, length: number, ...data: IFotoramaData[]): void;
  requestFullScreen(): void;
}

export interface IFotoramaBlockAPI {
  on(type: string, cb: () => void): void;
  data(name: string): IFotoramaAPI;
  unbind(): void;
  show(id: number): void;
}

interface IFotoramaData {
  img?: string;
  thumb?: string;
  video?: string;
  html?: string;
}

export type IFotoramaProps = IFotoramaStoreProps & IFotoramaDispatchProps;

interface IFotoramaApiExtended extends IFotoramaAPI {
  push(item: { html: string | HTMLElement; thumb?: string }): void;
}

export class Fotorama extends React.Component<IFotoramaProps, IFotoramaState> {
  private fotoramaDiv: HTMLElement | null;
  private $fotorama: IFotoramaBlockAPI | void;
  private fotorama: IFotoramaAPI | void;

  public componentDidMount() {
    this.fotoramaDiv = document.getElementById('frontend-offer-card-fotorama');
    const parameters = calculateParametersCombination(
      this.props.priceTotalRur || undefined,
      this.props.isApartments || false,
      this.props.isNewBuilding,
      this.props.offerType || undefined,
    );
    if (parameters) {
      getHtmlFotoramaComponent(parameters.price, parameters.type, parameters.subtype).then(({ response }) => {
        if (response) {
          const scriptElement = document.createElement('script');
          scriptElement.src = '//cdn.cian.site/finance/offer-fotorama-frame/widget.js';
          document.head.appendChild(scriptElement);
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = response;
          if (tempContainer.firstElementChild) {
            (this.fotorama as IFotoramaApiExtended).push({
              html: tempContainer.firstElementChild as HTMLElement,
              thumb: 'https://cdn.fin.cian.ru/static/cian.png',
            });
          }
        }
      });
    }
    if (window.$ && this.fotoramaDiv) {
      this.initFotorama();
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IFotoramaProps, prevProps: IFotoramaProps) {
    const { fotorama } = this;

    if (fotorama) {
      // move gallery to specific element
      if (nextProps.showElementId !== undefined) {
        fotorama.show(nextProps.showElementId);
        this.props.onElementShowed();
      }

      if (!prevProps.isFullScreen && nextProps.isFullScreen) {
        fotorama.requestFullScreen();
      }

      if (!this.props.floorPlanImage && nextProps.floorPlanImage && this.fotorama) {
        const { floorPlanImage } = nextProps;
        fotorama.splice(nextProps.videos.length, 0, { img: floorPlanImage, thumb: floorPlanImage });
      }
    }
  }

  public componentWillUnmount() {
    const { $fotorama } = this;

    if ($fotorama) {
      $fotorama.unbind();
    }
  }

  public componentDidUpdate() {
    const { fotorama } = this;

    if (this.props.isFullScreen) {
      /**
       * Запускаем видео если открыли fullscreen галерею на слайде с видео
       */
      const playVideoButton = document.querySelector('.fotorama__video-play');

      if (playVideoButton && fotorama) {
        fotorama.playVideo();
      }
    }
  }

  public render() {
    const { photos, isMicrodataExperimentEnable, floorPlanImage } = this.props;

    const itemsCount = this.props.photos.length + this.props.videos.length + Number(!!floorPlanImage);

    const photoStartIndex = Math.max(
      photos.findIndex(({ isDefault }) => isDefault),
      0,
    );
    const firstPhoto = photos[photoStartIndex];
    const urlFirstPhoto = firstPhoto && firstPhoto.fullUrl ? firstPhoto.fullUrl : '';

    const microData = isMicrodataExperimentEnable
      ? {
          itemScope: true,
          itemType: 'http://schema.org/ImageObject',
        }
      : {};

    return itemsCount > 0 ? (
      <div>
        {this.props.isFullScreen && this.fotorama && (
          <FullScreenControlsContainer exitFullscreen={this.fotorama.cancelFullScreen} />
        )}

        {/* Так как изменять DOM фоторамы не хорошо, кладем рядом невидимый span для микроразметки */}
        <span {...microData}>
          <span {...{ itemProp: 'url', content: urlFirstPhoto }} />
        </span>
        <div>
          <div
            className={styles['fotorama']}
            id="frontend-offer-card-fotorama"
            data-width="100%"
            data-height="402"
            data-nav="thumbs"
            data-fit="contain"
            data-keyboard="true"
            data-loop="true"
            data-arrows="true"
            data-swipe="false"
            data-thumbmargin="5"
            data-auto="false"
            data-allowfullscreen="true"
          />
        </div>
      </div>
    ) : (
      <div className={styles['default_content']} />
    );
  }

  private initFotorama() {
    const { photos, videos, floorPlanImage } = this.props;
    /**
     * Если есть видео, то первым отображаемым слайдом в галереи должно быть видео,
     * а не дефолтная фотография
     */
    const startIndex =
      videos.length > 0
        ? 0
        : Math.max(
            photos.findIndex(({ isDefault }) => isDefault),
            0,
          );

    const fotoramaBlock = window.$(this.fotoramaDiv);

    if (!fotoramaBlock || !fotoramaBlock.fotorama) {
      return;
    }

    /**
     * Если удалось получить блок fotoram'ы,
     * инициализируем галерею
     */
    const photoData = photos.map((photo: IPhotoSchema) => ({
      thumb: photo.thumbnailUrl,
      ...(!photo.isCianLayout
        ? {
            img: photo.fullUrl,
          }
        : {
            html: `
              <div class="${styles['cian-layout-wrp']}">
                <span>Типовая планировка квартиры от Циан</span>
                <div class="${styles['cian-layout-img']}" style="background-image: url(${photo.fullUrl})"></div>
              </div>
            `,
          }),
    }));

    if (floorPlanImage) {
      photoData.unshift({ img: floorPlanImage, thumb: floorPlanImage });
    }

    const videoData = videos.map((item: IVideoSchema) => ({ video: item.url }));

    // init fotorama
    this.$fotorama = fotoramaBlock.fotorama({
      data: [...videoData, ...photoData] as IFotoramaData[],
      startindex: startIndex,
    }) as IFotoramaBlockAPI;
    this.fotorama = this.$fotorama.data('fotorama');

    // add event listeners
    this.$fotorama.on('fotorama:load', () => {
      this.props.onInitialized();
    });

    this.$fotorama.on('fotorama:loadvideo', () => {
      this.props.onPlayVideo();
    });

    this.$fotorama.on('fotorama:unloadvideo', () => {
      this.props.onStopVideo();
    });

    this.$fotorama.on('fotorama:fullscreenenter', () => {
      const { fotorama } = this;

      if (fotorama) {
        this.props.onShowFullScreen(fotorama.activeIndex);

        trackFullScreenOpen();
      }
    });

    this.$fotorama.on('fotorama:fullscreenexit', () => {
      this.props.onHideFullScreen();
    });
  }
}
